import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useStore } from "../hooks.ts";

export interface EinkaufState {
  available: boolean;
  link: {
    peer: string;
    lists: { id: string; name: string; guests?: boolean }[];
    list: string | null;
    createdAt: number;
    lastSentAt: number | null;
    /** How the last send by itself went („Immer automatisch senden“, F19). */
    lastAuto?: { at: number; ok: boolean; added?: number; updated?: number; removed?: number; error?: string } | null;
  } | null;
}

export const when = (at: number) => new Date(at).toLocaleString("de-DE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** The message of a failed call, or a sentence for no connection. */
export const failText = (e: unknown) => (e instanceof Error && !/fetch|abort/i.test(e.message) ? e.message : "Ohne Verbindung zum Server geht das nicht.");

/**
 * Konto → EinkaufsZettel (docs/18-vorratsbuch.md, V1): a code made in the EinkaufsZettel (its
 * settings → VorratsBuch), entered here; then „Nachkaufen“ goes to the chosen list, and finished
 * trips come back to the Eingang. Owners and managers; only where the server knows an EinkaufsZettel.
 */
export function EinkaufLink() {
  const { store } = useStore();
  const [state, setState] = useState<EinkaufState | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    store
      .request<EinkaufState>("/api/workspace/einkauf")
      .then(setState)
      .catch((e) => setError(failText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state?.available) return error ? <p className="error-box">{error}</p> : null;
  const link = state.link;

  async function run(action: () => Promise<{ link: EinkaufState["link"] } | unknown>) {
    setBusy(true);
    setError(null);
    try {
      const r = (await action()) as { link?: EinkaufState["link"] };
      setState((s) => (s ? { ...s, link: r && "link" in r ? (r.link ?? null) : s.link } : s));
    } catch (e) {
      setError(failText(e));
    } finally {
      setBusy(false);
    }
  }

  const connect = (e: FormEvent) => {
    e.preventDefault();
    void run(() => store.request("/api/workspace/einkauf/connect", { method: "POST", body: JSON.stringify({ code }) })).then(() => setCode(""));
  };

  return (
    <section className="card" data-testid="einkauf">
      <h2>EinkaufsZettel</h2>
      <p className="muted">Gekoppelt geht „Nachkaufen“ von der Startseite auf eine Einkaufsliste, und was dort als gekauft abgeschlossen ist, erscheint im Eingang zum Einlagern.</p>
      {link ? (
        <>
          <p data-testid="einkauf-linked">
            Gekoppelt mit dem EinkaufsZettel-Haushalt <strong>{link.peer}</strong> seit {when(link.createdAt)}
            <br />
            <span className="muted">{link.lastSentAt ? `zuletzt gesendet ${when(link.lastSentAt)}` : "noch nichts gesendet"}</span>
          </p>
          {link.lists.length > 0 ? (
            <label>
              Nachkaufen auf die Liste
              <select value={link.list ?? ""} disabled={busy} onChange={(e) => void run(() => store.request("/api/workspace/einkauf/list", { method: "PUT", body: JSON.stringify({ list: e.target.value }) }))} data-testid="einkauf-list">
                {link.lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.guests ? " (mit Gästen)" : ""}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="notice">Im EinkaufsZettel ist keine Liste für VorratsBuch freigegeben.</p>
          )}
          <button
            className="link danger"
            disabled={busy}
            onClick={() => confirm("Kopplung mit dem EinkaufsZettel lösen?") && void run(() => store.request("/api/workspace/einkauf", { method: "DELETE" }).then(() => ({ link: null })))}
            data-testid="einkauf-unlink"
          >
            Kopplung lösen
          </button>
        </>
      ) : (
        <form className="row" noValidate onSubmit={connect}>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code aus dem EinkaufsZettel, z. B. 4F7K-2QXM" autoComplete="off" data-testid="einkauf-code" />
          <button className="primary" disabled={busy || code.trim() === ""} data-testid="einkauf-connect">
            Koppeln
          </button>
        </form>
      )}
      {!link && <p className="hint">Den Code erzeugt ein Admin im EinkaufsZettel unter Einstellungen → VorratsBuch; er gilt 15 Minuten.</p>}
      {error && <p className="error-box">{error}</p>}
    </section>
  );
}
